import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { changeColorAlpha, useEventListener } from '@elfsight/lib-utils';
import { getHandleActionKeyDown } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { classnamePrefix, ICONS_URL } from '../../../constants';

export const Share = ({ items, link, popup = false, mobile = false }) => {
  const [show, toggleShow] = useState(false);
  const [direction, setDirection] = useState('left');
  const shareRef = useRef(null);
  const buttonRef = useRef(null);
  const { textColor } = useSettings();

  const shareItems = items || [
    {
      title: 'Copy link',
      iconAlt: 'Copy icon',
      icon: `${ICONS_URL}-copy-icon.svg`,
      handler: {
        function: () => {
          const textarea = document.createElement('textarea');

          textarea.textContent = link;
          textarea.style.position = 'fixed';

          document.body.appendChild(textarea);

          textarea.select();

          try {
            return document.execCommand('copy');
          } catch (ex) {
            return false;
          } finally {
            document.body.removeChild(textarea);
          }
        }
      }
    },
    {
      title: 'Share on Facebook',
      iconAlt: 'Facebook icon',
      icon: `${ICONS_URL}-facebook-share-icon-multicolor.svg`,
      handler: {
        window: {
          url: `https://www.facebook.com/sharer/sharer.php?u=${link}`
        }
      }
    },
    {
      title: 'Share on X',
      iconAlt: 'X icon',
      icon: `${ICONS_URL}-x-share-icon.svg`,
      handler: {
        window: {
          url: `https://x.com/intent/tweet?url=${link}`
        }
      }
    }
  ];

  function isDescendant(parent, child) {
    let node = child.parentNode;

    while (node !== null) {
      if (node === parent) {
        return true;
      }

      node = node.parentNode;
    }
    return false;
  }

  useEventListener(
    'click',
    (e) => {
      if (!isDescendant(shareRef.current, e.target) && show) {
        toggleShow(false);
      }
    },
    document
  );

  useEffect(() => {
    if (show && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      if (rect.left < 160) {
        setDirection('right');
      } else {
        setDirection('left');
      }
    }
  }, [show]);

  const onClick = (e, handler) => {
    if (handler.window) {
      const openWindow = (url, title, w = 600, h = 600) => {
        const windowSize = {
          width: w,
          height: h
        };

        const windowLocation = {
          left:
            window.screen.availLeft +
            window.screen.availWidth / 2 -
            windowSize.width / 2,
          top:
            window.screen.availTop +
            window.screen.availHeight / 2 -
            windowSize.height / 2
        };

        const windowOptions = [
          `width=${windowSize.width}`,
          `height=${windowSize.height}`,
          'menubar=no',
          'toolbar=no',
          'resizable=no',
          'scrollbars=yes',
          `left=${windowLocation.left}`,
          `top=${windowLocation.top}`
        ];

        const popupItem = window.open(url, title, windowOptions.join(','));

        if (window.focus) {
          popupItem.focus();
        }

        return popupItem;
      };

      openWindow(handler.window.url, 'share');
    } else if (handler.function) {
      handler.function();
    }

    toggleShow(false);

    e.stopPropagation();
  };

  return (
    <Component ref={shareRef}>
      <Window _show={show} _direction={direction}>
        <Items>
          {shareItems.map((item, id) => (
            <Item key={id} onClick={(e) => onClick(e, item.handler)}>
              {item.icon && (
                <ItemIcon>
                  <img src={item.icon} alt={item.iconAlt} />
                </ItemIcon>
              )}

              <ItemTitle>{item.title}</ItemTitle>
            </Item>
          ))}
        </Items>
      </Window>

      <Button
        ref={buttonRef}
        onClick={() => {
          toggleShow(!show);
        }}
        tabIndex="0"
        onKeyDown={getHandleActionKeyDown(() => {
          toggleShow(!show);
        })}
        _isInPopup={popup}
        _textColor={textColor}
      >
        <ButtonIcon
          width="12px"
          height="12px"
          viewBox="0 0 24 24"
          _isMobile={mobile}
          _textColor={textColor}
        >
          <path
            id="XMLID_31_"
            d="M12.7,15.3c-4.5,0.1-8.4,2.5-10.7,6c-0.2,0.3-0.6,0.5-0.9,0.5c-0.1,0-0.2,0-0.3,0c-0.5-0.1-0.8-0.6-0.8-1c0,0,0-0.1,0-0.1c0-7.1,5.7-12.9,12.7-13V5.5c0-0.5,0.3-0.9,0.7-1.1c0.2-0.1,0.4-0.1,0.6-0.1c0.3,0,0.5,0.1,0.7,0.2l8.8,6c0.3,0.2,0.5,0.6,0.5,0.9c0,0.4-0.2,0.7-0.5,1l-8.8,6.1c-0.2,0.2-0.5,0.2-0.7,0.2c-0.2,0-0.4,0-0.6-0.1c-0.4-0.2-0.7-0.7-0.7-1.1V15.3z"
          />
        </ButtonIcon>

        <ButtonText
          _isMobile={mobile}
          _textColor={textColor}
          _isInPopup={popup}
        >
          Share
        </ButtonText>
      </Button>
    </Component>
  );
};

const Component = styled.div.attrs({
  className: `${classnamePrefix}-share-component`
})`
  position: relative;
`;

const Window = styled.div.attrs({
  className: `${classnamePrefix}-share-window`
})`
  position: absolute;
  width: 160px;
  margin-bottom: 8px;
  background: #fff;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.3);
  border: 1px solid #eee;
  border-radius: 6px;
  overflow: hidden;
  transition:
    opacity 0.3s ease,
    visibility ease 0.3s,
    bottom 0.3s ease;
  opacity: ${({ _show }) => (_show ? 1 : 0)};
  pointer-events: ${({ _show }) => (_show ? 'all' : 'none')};
  visibility: ${({ _show }) => (_show ? 'visible' : 'hidden')};
  bottom: ${({ _show }) => (_show ? 'calc(100%)' : 'calc(100% - 5px)')};
  ${({ _direction }) => (_direction === 'left' ? 'right: 0;' : 'left: 0;')};
`;

const Items = styled.div``;

const Item = styled.div.attrs({
  className: `${classnamePrefix}-share-item`
})`
  display: flex;
  align-items: center;
  color: #17191a;
  font-size: 13px;
  font-weight: 400;
  line-height: 1;
  border-top: 1px solid #e5e6e7;
  cursor: pointer;
  position: relative;
  text-align: left;
  transition:
    background 0.3s ease,
    border-radius 0.3s ease;
  background-color: transparent;
  padding: 8px 0;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  &:hover {
    background: #fafafa;
    border-radius: 4px;
  }

  & img {
    height: 12px;
  }
`;

const ItemIcon = styled.div.attrs({
  className: `${classnamePrefix}-share-itemIcon`
})`
  height: 100%;
  width: 12px;
  float: left;
  align-items: center;
  justify-content: center;
  display: inline-flex;
  margin-left: 12px;
`;

const ItemTitle = styled.div.attrs({
  className: `${classnamePrefix}-share-itemTitle`
})`
  font-size: 11px;
  line-height: 16px;
  color: rgba(17, 17, 17, 0.7);
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  padding-right: 5px;
  display: inline-block;
  margin-left: 12px;
`;

const Button = styled.div.attrs({
  className: `${classnamePrefix}-share-button`
})`
  position: relative;
  display: inline-flex;
  cursor: pointer;
  padding: 2px 12px;
  border-radius: 2px;
  text-decoration: none;
  transition:
    background-color 0.2s ease,
    color 0.2s ease,
    fill 0.2s ease;
  user-select: none;
  box-sizing: content-box;
  white-space: nowrap;
  justify-content: center;
  align-items: center;

  color: ${({ _isInPopup, _textColor }) => (!_isInPopup ? _textColor : '')};
  background-color: ${({ _isInPopup, _textColor }) =>
    !_isInPopup && _textColor
      ? changeColorAlpha(_textColor, -0.94)
      : 'rgba(26, 46, 59, 0.06)'};

  &:hover {
    background-color: ${({ _isInPopup, _textColor }) =>
      !_isInPopup && _textColor
        ? changeColorAlpha(_textColor, -0.8)
        : 'rgba(26, 46, 59, 0.2)'};
  }

  & svg {
    fill: ${({ _isInPopup, _textColor }) => (!_isInPopup ? _textColor : '')};
  }
`;

const ButtonIcon = styled.svg.attrs({
  className: `${classnamePrefix}-share-buttonIcon`
})`
  display: inline-flex;
  ${({ _isMobile }) =>
    _isMobile &&
    css`
      width: 8px;
      height: 8px;
    `}
`;

const ButtonText = styled.div.attrs({
  className: `${classnamePrefix}-share-buttonText`
})`
  display: inline-flex;
  margin-left: 6px;
  text-align: center;
  line-height: ${({ _isMobile }) => (_isMobile ? '16px' : '20px')};
  ${({ _isInPopup, _textColor }) => !_isInPopup && `color: ${_textColor}`}
  ${({ _isMobile }) => _isMobile && 'font-size: 11px'}
`;
